import type { Preferences } from '@cobliteam/common-types';
import { alertHookConfig } from '@cobliteam/hooks/src/useAlertHooks/alertHookConfig';
import type { CurrentAlertHook } from '@cobliteam/hooks/src/useAlertHooks/types';
import useAlertHooksStore from '@cobliteam/hooks/src/useAlertHooks/useAlertHooksStore';
import useLocalStorage from '@cobliteam/hooks/src/useLocalStorage';
import { useTranslation } from '@cobliteam/i18n';
import { Button, Dialog, Typography } from '@cobliteam/ui/components';
import { Checkbox, Label } from '@cobliteam/ui/form';
import Icon from '@cobliteam/ui/icons';
import { useState } from 'react';
import { trackDismissed, trackExperienceViewed } from './analytics';

interface Benefit {
  icon: string;
  label: string;
  description?: string;
}

interface DialogAlertHookSuggestionProps {
  current: CurrentAlertHook;
}

/**
 * Step 1 — pitches the alert. "Ver sugestão de alerta" advances to the preview step
 * (DialogAlertHookConfirm), where the trigger is actually created.
 *
 * The checkbox below the benefits is the **global** opt-out: ticking it and clicking "Ignorar"
 * persists `hideAlertHookSuggestions=true` on the local preferences, which short-circuits every
 * future call to `useTriggerAlertHook`.
 */
const DialogAlertHookSuggestion = ({ current }: DialogAlertHookSuggestionProps) => {
  const { t } = useTranslation(['alertHooks']);
  const advanceToConfirm = useAlertHooksStore((state) => state.advanceToConfirm);
  const dismiss = useAlertHooksStore((state) => state.dismiss);
  const [preferences, setPreferences] = useLocalStorage<Preferences>('preferences');

  const [dontShowAgain, setDontShowAgain] = useState(false);

  const config = alertHookConfig[current.type];
  const { copy } = config;
  const dismissKey = config.getDismissKey(current.params);
  const alertType = config.kind === 'blueprint' ? { alert_type: config.alertType } : {};
  const benefits = t(copy.suggestion.benefitsKey, { returnObjects: true, defaultValue: [] }) as Benefit[];

  const handleSeeExperience = () => {
    trackExperienceViewed({ hookType: current.type, dismissKey, ...alertType });
    advanceToConfirm();
  };

  const handleDismiss = () => {
    trackDismissed({ hookType: current.type, dismissKey, dontShowAgain, ...alertType });
    if (dontShowAgain) {
      setPreferences({ ...(preferences ?? ({} as Preferences)), hideAlertHookSuggestions: true });
    }
    dismiss();
  };

  return (
    <Dialog.Content size='sm' className='flex flex-col gap-0 p-0'>
      <Dialog.Body className='px-6 pt-2 pb-4'>
        <div className='mb-4 flex flex-col items-center justify-center pt-8 text-center'>
          <div className={`flex h-14 w-14 items-center justify-center rounded-full ${copy.suggestion.iconBg}`}>
            <Icon name={copy.suggestion.iconName as never} size='lg' color='primary' />
          </div>
          <Dialog.Title className='mt-4 text-center'>{t(copy.suggestion.titleKey)}</Dialog.Title>
          <Dialog.Description asChild>
            <Typography variant='text-sm' color='secondary' className='mt-2 px-6 text-center'>
              {t(copy.suggestion.descriptionKey)}
            </Typography>
          </Dialog.Description>
        </div>

        <div className='rounded-2xl border border-gray-6 bg-gray-2 p-5'>
          <Typography variant='text-sm-medium' className='mb-3 block'>
            {t('alertHooks:common.benefitsTitle')}
          </Typography>
          <ul className='flex flex-col gap-3'>
            {benefits.map((benefit) => (
              <li key={benefit.label} className='flex items-start gap-3'>
                <Icon name={benefit.icon as never} size='sm' color='primary' className='mt-0.5 shrink-0' />
                <div className='flex flex-col gap-0.5'>
                  <Typography variant='text-sm-medium'>{benefit.label}</Typography>
                  {benefit.description && (
                    <Typography variant='text-sm' color='secondary'>
                      {benefit.description}
                    </Typography>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className='mt-5 flex items-center justify-center gap-2'>
          <Checkbox id='dialog-alert-hook-dont-show-again' checked={dontShowAgain} onCheckedChange={(value) => setDontShowAgain(value === true)} />
          <Label htmlFor='dialog-alert-hook-dont-show-again' className='cursor-pointer'>
            <Typography variant='text-sm' color='secondary'>
              {t('alertHooks:common.dontShowAgain')}
            </Typography>
          </Label>
        </div>
      </Dialog.Body>

      <Dialog.Footer className='flex flex-col gap-2 px-6 pt-4'>
        <Button onClick={handleSeeExperience} className='w-full' startIcon={<Icon name='eye' />}>
          {t('alertHooks:common.ctaSee')}
        </Button>
        <Button onClick={handleDismiss} variant='ghost' className='w-full'>
          {t('alertHooks:common.ctaDismiss')}
        </Button>
      </Dialog.Footer>
    </Dialog.Content>
  );
};

export default DialogAlertHookSuggestion;
